// src/modals/UnifiedDateActionModal.jsx — Phase 6.B PR #167a (ADR-099) + PR #167b amend (ADR-100)
//
// Modal unifiée à onglets [💰 Prix] [🔒 Bloquer] (Option Hybride).
//
// PR #167a = refactor pur : lockTab toujours actif depuis CalendarPanel
// (clic cellule → lockTab='prix' ; bouton header → lockTab='bloquer').
// Onglets masqués à l'écran en pratique (UX identique à avant).
//
// PR #167b amend (ADR-100) — au clic cellule manual, lockTab est levé →
// onglets visibles + switchables. L'user peut basculer Prix ↔ Bloquer dans
// la même modal. Pre-fill logement + date au switch tab Bloquer (via
// initialLogementId/initialStartDate passés au body Bloquer).
// Bouton header conserve lockTab='bloquer' → UX inchangé.
//
// Confirmation form dirty au switch tab : avant de switcher, si le body actif
// a un form dirty (state non vide non submitted), un window.confirm() demande
// confirmation. Si user refuse → stay sur l'onglet actuel. Si accepte → switch
// (le body destination est re-mount via `key` propre, donc fresh).
//
// Source unique Esc + click-overlay quand utilisée directement depuis
// CalendarPanel (BPRa-3 : 0 listener concurrent).
// Aucun useDailyPricing() ici (BPRa-2 : pas de cache parallèle, invalidate
// reçue en prop = globalInvalidate hissée CalendarPanel).

import { useCallback, useEffect, useRef, useState } from "react";
import FeuilleConfirmation from "../components/FeuilleConfirmation";
import { Coins, Lock } from "lucide-react";
import { DS } from "../lib/ui";
// Import alias en PascalCase pour ESLint react/jsx-pascal-case ; les fichiers
// gardent le préfixe `_` (convention `_block-helpers.js` = interne, ne pas
// importer directement depuis l'extérieur du module modals).
import PricingManualEditBody from "./_PricingManualEditBody";
import PricingBlockCreateBody from "./_PricingBlockCreateBody";

/**
 * Modal unifiée actions de date du calendrier.
 * @param {object} props
 * @param {boolean} props.open
 * @param {'prix'|'bloquer'} [props.initialTab='prix']
 * @param {'prix'|'bloquer'|null} [props.lockTab=null] — si défini, masque la tab bar et force le tab
 * @param {string} [props.logementId] — pré-fill body Prix (clic cellule) + body Bloquer (PR #167b amend)
 * @param {string} [props.logementLabel] — affichage body Prix
 * @param {string} [props.dateISO] — pré-fill body Prix (date_from = date_to = dateISO)
 *                                    + body Bloquer (start_date = end_date = dateISO, PR #167b amend)
 * @param {Array} [props.allLogs] — passé au body Bloquer
 * @param {Map} [props.modesByLogement] — passé au body Bloquer
 * @param {Map} [props.pricingMap] — PR #167b : pre-fill price/min_stay/fetched_at body Prix
 * @param {boolean} props.dryRunActive
 * @param {function} [props.invalidate] — globalInvalidate hissée CalendarPanel (BPRa-2)
 *                                        Aussi passée au body Prix comme onReload (PR #167b).
 * @param {function} props.onClose
 * @param {function} props.onSaved — signature ({ ok, mode?, ...json }) — mode injecté
 *                                    par wrapper pour discriminer toast post-save Prix vs Bloquer.
 */
export default function UnifiedDateActionModal({
  open,
  initialTab = "prix",
  lockTab = null,
  logementId,
  logementLabel,
  dateISO,
  allLogs,
  modesByLogement,
  pricingMap,
  dryRunActive,
  invalidate,
  onClose,
  onSaved,
}) {
  // Tab state : init = lockTab si défini, sinon initialTab.
  const [activeTab, setActiveTab] = useState(lockTab || initialTab);

  // PR #167b amend — Dirty flags par tab (notifiés par chaque body via
  // onDirtyChange). Stocké en ref pour éviter re-render sur chaque touche.
  // Reset à l'ouverture de la modal.
  const formDirtyRef = useRef({ prix: false, bloquer: false });

  const handleDirtyChangePrix = useCallback((isDirty) => {
    formDirtyRef.current.prix = !!isDirty;
  }, []);
  const handleDirtyChangeBloquer = useCallback((isDirty) => {
    formDirtyRef.current.bloquer = !!isDirty;
  }, []);

  // Reset tab + dirty refs à chaque ouverture (transition `open: false → true`)
  useEffect(() => {
    if (open) {
      setActiveTab(lockTab || initialTab);
      formDirtyRef.current = { prix: false, bloquer: false };
    }
  }, [open, lockTab, initialTab]);

  // PR #167b amend — Switch tab avec confirmation si form dirty.
  // Le body actif est consulté via formDirtyRef. Si dirty + destination !=
  // active, demande confirmation explicite. Si accepte → reset dirty source
  // (le body destination sera re-mount via key, donc fresh).
  // ⚠️ NOTRE GRAMMAIRE (Dimitri 2026-08-22) : plus de boîte du navigateur — une
  // feuille sur téléphone, une carte centrée sur ordinateur, et une phrase qui
  // dit ce qu'on perd.
  const [ongletDemande, setOngletDemande] = useState(null);
  const requestSwitchTab = useCallback(
    (targetTab) => {
      if (targetTab === activeTab) return;
      if (formDirtyRef.current[activeTab]) { setOngletDemande(targetTab); return; }
      formDirtyRef.current[activeTab] = false;
      setActiveTab(targetTab);
    },
    [activeTab],
  );

  const confirmerChangementOnglet = useCallback(() => {
    const cible = ongletDemande;
    setOngletDemande(null);
    if (!cible) return;
    // Reset dirty de l'onglet source (re-mount destination = fresh state).
    formDirtyRef.current[activeTab] = false;
    setActiveTab(cible);
  }, [ongletDemande, activeTab]);

  // BPRa-3 : Esc handler unique source (le wrapper thin n'en pose pas si délégué ici).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const showTabs = !lockTab;
  // Largeur max différenciée selon tab (Bloquer = form plus large)
  const maxWidth = activeTab === "bloquer" ? 480 : 420;

  return (
    <>
    {ongletDemande && (
      <FeuilleConfirmation
        testId="unified-changer-onglet"
        titre="Quitter cet onglet sans enregistrer ?"
        texte="Ce que vous venez de saisir ici sera perdu. L'autre onglet s'ouvrira vide de cette saisie."
        libelleConfirmer="Quitter sans enregistrer"
        libelleRenoncer="Rester ici"
        ton="danger"
        onConfirmer={confirmerChangementOnglet}
        onRenoncer={() => setOngletDemande(null)} />
    )}
    <div
      data-testid="unified-date-action-modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.5)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        fontFamily: DS.fontBody,
      }}
    >
      <div
        data-testid="unified-date-action-modal-container"
        className="fbz-modal-sheet"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: DS.card,
          borderRadius: DS.radiusCard,
          maxWidth,
          width: "100%",
          padding: 22,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        {/* Tab bar : masquée si lockTab actif (Q2=B). Rendue pour préparer #167b. */}
        {showTabs && (
          <div
            data-testid="unified-modal-tabs"
            style={{
              display: "flex",
              gap: 4,
              marginBottom: 16,
              borderBottom: `1px solid ${DS.border}`,
            }}
          >
            <button
              type="button"
              data-testid="unified-modal-tab-prix"
              onClick={() => requestSwitchTab("prix")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "transparent",
                color: activeTab === "prix" ? DS.corail : DS.textMuted,
                border: "none",
                borderBottom: `2px solid ${activeTab === "prix" ? DS.corail : "transparent"}`,
                marginBottom: -1,
                padding: "8px 14px",
                fontSize: 12,
                fontWeight: activeTab === "prix" ? 700 : 600,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              <Coins size={14} strokeWidth={2.2} /> Prix
            </button>
            <button
              type="button"
              data-testid="unified-modal-tab-bloquer"
              onClick={() => requestSwitchTab("bloquer")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "transparent",
                color: activeTab === "bloquer" ? DS.corail : DS.textMuted,
                border: "none",
                borderBottom: `2px solid ${activeTab === "bloquer" ? DS.corail : "transparent"}`,
                marginBottom: -1,
                padding: "8px 14px",
                fontSize: 12,
                fontWeight: activeTab === "bloquer" ? 700 : 600,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              <Lock size={14} strokeWidth={2.2} /> Bloquer
            </button>
          </div>
        )}

        {/* Body actif. Key permet le re-mount au switch tab (reset state propre). */}
        {activeTab === "prix" && (
          <PricingManualEditBody
            key="prix"
            open={open}
            logementId={logementId}
            logementLabel={logementLabel}
            dateFrom={dateISO}
            dateTo={dateISO}
            dryRunActive={dryRunActive}
            pricingMap={pricingMap}
            onReload={invalidate}
            onDirtyChange={handleDirtyChangePrix}
            onClose={onClose}
            onSaved={(result) =>
              onSaved && onSaved({ ...(result || {}), mode: "prix" })
            }
          />
        )}
        {activeTab === "bloquer" && (
          <PricingBlockCreateBody
            key="bloquer"
            open={open}
            allLogs={allLogs}
            modesByLogement={modesByLogement}
            dryRunActive={dryRunActive}
            initialLogementId={logementId}
            initialStartDate={dateISO}
            onDirtyChange={handleDirtyChangeBloquer}
            onClose={onClose}
            onSaved={(result) =>
              onSaved && onSaved({ ...(result || {}), mode: "bloquer" })
            }
            invalidate={invalidate}
          />
        )}
      </div>
    </div>
    </>
  );
}
